import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ClassroomStudentActivityState, WindowData } from "@/js/components/RegularClassroomUser/utils";
import { Box, Flex, Image, Text } from "@chakra-ui/react";
import loadingDots from "@/assets/lottie/loading-dots.lottie?url";
import IconTileContentLayout from "@/js/components/StudentTile/components/IconTileContentLayout";
import { getCopy, getIcon } from "@/js/utils/screens/contextBasedScreenUtils";
import { P2pTabData } from "@/js/components/P2PDisplayModal/components/types";
import { restrictedScreenshotImage } from "@/js/components/StudentTile/components/activeScreenStates/restrictedScreenshot";
import { P2pImageWrapper } from "@/js/components/P2PDisplayModal/components/P2pImageWrapper";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

interface ContextBasedStudentTileProps {
	window: WindowData;
	currentTab: P2pTabData | undefined;
	imageData?: string;
	fullscreen: boolean;
	username: string;
	screenshottable: boolean;
	screenshotState: ClassroomStudentActivityState;
}

export const ContextBasedP2PWindow: React.FC<ContextBasedStudentTileProps> = ({
	window,
	fullscreen,
	username,
	currentTab,
	imageData,
	screenshottable,
	screenshotState,
}: ContextBasedStudentTileProps) => {
	const { t } = useTranslation();
	const { state } = window;
	const url = useMemo(() => (currentTab ? currentTab.tabUrl : window.url), [window.url, currentTab]);
	const copyKey = useMemo(() => getCopy(url, state), [url, state]);
	const copy = copyKey ? t(copyKey) : undefined;
	const icon = useMemo(() => getIcon(url, state), [url, state]);
	const screenshot = useMemo(() => {
		if (currentTab) {
			if (!imageData && currentTab.tab_id === window.tab_id && !screenshottable) {
				return window.screenshot;
			}
			return imageData;
		}
		return window.screenshot;
	}, [window.screenshot, imageData, currentTab, window.tab_id, screenshottable]);

	const hasError = useMemo(() => {
		return (
			!!state &&
			state !== ClassroomStudentActivityState.SCREEN_AVAILABLE &&
			state !== ClassroomStudentActivityState.CACHED_SCREEN_AVAILABLE &&
			state !== ClassroomStudentActivityState.UNSET
		);
	}, [state]);

	const renderMessagingTile = (message: string, subtitle: string | undefined, iconComponent: JSX.Element) => (
		<Flex
			flexDirection={"column"}
			align={"center"}
			height={"210px"}
			width={"448px"}
			py={"40px"}
			px={"60px"}
			backgroundColor={"white"}
			borderRadius={"6px"}
			className={fullscreen ? "P2PDisplayModal_screenBox--fullscreen" : "P2PDisplayModal_screenBox"}
		>
			<IconTileContentLayout>
				{iconComponent}
				<Text fontWeight={"bold"} fontSize={"20px"}>
					{message}
				</Text>
				{subtitle && <Text> {subtitle}</Text>}
			</IconTileContentLayout>
		</Flex>
	);

	if (state === ClassroomStudentActivityState.SCREEN_CAPTURE_RESTRICTED) {
		return (
			<P2pImageWrapper>
				<Box className="P2PDisplayModal_loadingTile">
					<Image
						width={"100%"}
						height={"100%"}
						objectFit={"contain"}
						src={restrictedScreenshotImage}
						alt={t("p2pDisplay.screenAlt", { username })}
					/>
				</Box>
			</P2pImageWrapper>
		);
	}

	const screenshotStateError =
		screenshotState !== ClassroomStudentActivityState.SCREEN_AVAILABLE && screenshotState !== ClassroomStudentActivityState.UNSET;

	if ((hasError && !screenshottable) || (screenshotStateError && screenshottable)) {
		return (
			<P2pImageWrapper>
				{renderMessagingTile(
					copy ?? t("p2pDisplay.noScreenshotAvailable"),
					copy ? t("p2pDisplay.screenshotsNotDisplayedSubtitle") : "",
					<Image src={icon} htmlHeight={"64px"} htmlWidth={"64px"} alt={t("p2pDisplay.screenAlt", { username })} />
				)}
			</P2pImageWrapper>
		);
	}

	return (
		<P2pImageWrapper>
			{screenshot ? (
				<Box className="P2PDisplayModal_loadingTile">
					<Image width={"100%"} height={"100%"} objectFit={"contain"} src={screenshot} alt={t("p2pDisplay.screenAlt", { username })} />
				</Box>
			) : (
				renderMessagingTile(
					t("p2pDisplay.loading"),
					undefined,
					<DotLottieReact
						src={loadingDots}
						loop
						style={{ width: "46px", height: "46px" }}
						autoplay
						renderConfig={{ autoResize: true, freezeOnOffscreen: false }}
					/>
				)
			)}
		</P2pImageWrapper>
	);
};
